/**
 * Dailymotion video library — request building + page fetching.
 * Kept in TypeScript (no Redux) so it is unit-testable with the repo jest
 * config, which only transforms .ts/.tsx files.
 */
import restDailymotion from './restDailymotion';
import {
  DM_CHANNELS,
  DM_DEFAULT_SORT,
  DM_FIELDS,
  DM_MAX_PAGE,
  DM_PAGE_SIZE,
  DM_SORTS,
  DmSort,
} from './constants';
import { isEmbeddable } from './utils';

// Global videos endpoint, restricted to our channels through `owners`: one
// request returns a single list merged and sorted across all channels.
export const LIST_URL = '/videos';

const ALL_OWNERS = DM_CHANNELS.map(c => c.id).join(',');

/**
 * `owners` value for the channel filter: the selected channel when it is one
 * of ours, otherwise all our channels. Never empty — without `owners` the
 * endpoint would search the whole Dailymotion catalog.
 */
export const ownersFor = (channel?: string): string =>
  DM_CHANNELS.some(c => c.id === channel) ? (channel as string) : ALL_OWNERS;

/**
 * Signature of the current query (search + sort + channel). Carried through the
 * request lifecycle so the reducer can drop stale responses (e.g. a "load more"
 * reply that lands after the user already changed the search or the channel)
 * instead of appending them to the wrong list.
 */
export const signatureOf = (search: string, sort: string, channel?: string): string =>
  `${sort}|${channel || ''}|${search || ''}`;

/**
 * Sort actually sent to the API: an unknown sort falls back to the default,
 * and `relevance` needs a search term (the API answers HTTP 400 otherwise).
 */
export const effectiveSort = (sort: string, search: string): DmSort => {
  if (DM_SORTS.indexOf(sort as DmSort) === -1) {
    return DM_DEFAULT_SORT;
  }
  return sort === 'relevance' && !search ? DM_DEFAULT_SORT : (sort as DmSort);
};

/**
 * Build the query params for a listing/search call over our channels.
 * `page` is clamped to [1, DM_MAX_PAGE] because the API returns HTTP 400 for
 * page > 100. `total` is intentionally never sent (the API rejects it and its
 * count is unreliable anyway) — we paginate on `has_more`.
 */
export const buildParams = (search: string, sort: string, page: number, channel?: string) => {
  const params: { [key: string]: string | number } = {
    owners: ownersFor(channel),
    fields: DM_FIELDS,
    sort: effectiveSort(sort, search),
    limit: DM_PAGE_SIZE,
    page: Math.max(1, Math.min(DM_MAX_PAGE, page)),
  };
  if (search) {
    params.search = search;
  }
  return params;
};

/** Fetch one page, keeping only videos the article player can play. */
export const fetchPage = (search: string, sort: string, page: number, channel?: string) =>
  restDailymotion.get(LIST_URL, { params: buildParams(search, sort, page, channel) }).then(res => ({
    list: (res.data.list || []).filter(isEmbeddable),
    hasMore: Boolean(res.data.has_more),
  }));
